'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdThermostat,
    MdWarning,
    MdCheckCircle,
    MdRefresh,
    MdNotifications,
} from 'react-icons/md'
import {
    getStorageUnits,
    getTemperatureAlerts,
    StorageUnit,
    TemperatureAlert,
    STORAGE_UNIT_TYPE_LABELS,
    TEMPERATURE_ALERT_STATUS_LABELS,
    TEMPERATURE_ALERT_STATUS_COLORS,
} from '@/lib/api/stock/temperature-api'

export default function TemperaturePage() {
    const navigate = useNavigate()
    const [storageUnits, setStorageUnits] = useState<StorageUnit[]>([])
    const [alerts, setAlerts] = useState<TemperatureAlert[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'units' | 'alerts'>('units')

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            const [unitsResponse, alertsResponse] = await Promise.all([
                getStorageUnits({ isActive: true }),
                getTemperatureAlerts({ status: 'TEMP_ALERT_ACTIVE' }),
            ])

            if (unitsResponse.success) {
                setStorageUnits(unitsResponse.result)
            }

            if (alertsResponse.success) {
                setAlerts(alertsResponse.result)
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const getTemperatureStatus = (unit: StorageUnit) => {
        const lastLog = unit.temperatureLogs?.[0]
        if (!lastLog) return { status: 'unknown', color: 'ghost' }

        const temp = lastLog.temperature
        if (temp < unit.criticalMinTemp || temp > unit.criticalMaxTemp) {
            return { status: 'critical', color: 'error' }
        }
        if (temp < unit.minTemp || temp > unit.maxTemp) {
            return { status: 'warning', color: 'warning' }
        }
        return { status: 'normal', color: 'success' }
    }

    const formatDateTime = (dateStr: string) => {
        return new Date(dateStr).toLocaleString('pt-BR')
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando monitoramento...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-info to-info/70 flex items-center justify-center shadow-lg">
                        <MdThermostat className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Monitoramento de Temperatura
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {storageUnits.length} unidades de armazenamento
                            {alerts.length > 0 && (
                                <span className="text-error ml-2">
                                    | {alerts.length} alertas ativos
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={loadData}
                    >
                        <MdRefresh className="text-lg" />
                        Atualizar
                    </button>
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/stock/temperature/unidades/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Nova Unidade</span>
                    </button>
                </div>
            </div>

            <div className="tabs tabs-boxed bg-base-100 p-1">
                <button
                    className={`tab ${activeTab === 'units' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('units')}
                >
                    Unidades ({storageUnits.length})
                </button>
                <button
                    className={`tab ${activeTab === 'alerts' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('alerts')}
                >
                    <MdNotifications className="mr-1" />
                    Alertas ({alerts.length})
                </button>
            </div>

            {activeTab === 'units' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {storageUnits.map(unit => {
                        const tempStatus = getTemperatureStatus(unit)
                        const lastLog = unit.temperatureLogs?.[0]

                        return (
                            <div
                                key={unit.id}
                                className="bg-base-100 rounded-xl border border-base-300/50 p-4 hover:shadow-lg transition-shadow cursor-pointer"
                                onClick={() =>
                                    navigate(
                                        `/stock/temperature/unidades/${unit.id}`
                                    )
                                }
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <h3 className="font-semibold text-lg">
                                            {unit.name}
                                        </h3>
                                        <p className="text-sm text-base-content/60">
                                            {STORAGE_UNIT_TYPE_LABELS[unit.type]}
                                        </p>
                                    </div>
                                    {tempStatus.status === 'critical' && (
                                        <MdWarning className="text-error text-2xl animate-pulse" />
                                    )}
                                    {tempStatus.status === 'warning' && (
                                        <MdWarning className="text-warning text-2xl" />
                                    )}
                                    {tempStatus.status === 'normal' && (
                                        <MdCheckCircle className="text-success text-2xl" />
                                    )}
                                </div>

                                <div className="flex items-center justify-center py-4">
                                    <div className="text-center">
                                        <div
                                            className={`text-4xl font-bold text-${tempStatus.color}`}
                                        >
                                            {lastLog
                                                ? `${lastLog.temperature.toFixed(1)}C`
                                                : '--'}
                                        </div>
                                        {lastLog?.humidity && (
                                            <div className="text-sm text-base-content/60">
                                                {lastLog.humidity.toFixed(0)}%
                                                umidade
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="text-xs text-base-content/50 border-t border-base-300/50 pt-3 mt-3">
                                    <div className="flex justify-between">
                                        <span>
                                            Faixa: {unit.minTemp}C -{' '}
                                            {unit.maxTemp}C
                                        </span>
                                        {lastLog && (
                                            <span>
                                                {formatDateTime(
                                                    lastLog.timestamp
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}

                    {storageUnits.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center min-h-[300px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                            <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                                <MdThermostat className="text-4xl text-base-content/30" />
                            </div>
                            <div className="text-center">
                                <h3 className="text-lg font-semibold text-base-content mb-1">
                                    Nenhuma unidade cadastrada
                                </h3>
                                <p className="text-sm text-base-content/60 mb-4">
                                    Cadastre unidades de armazenamento para
                                    monitorar a temperatura
                                </p>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/stock/temperature/unidades/new'
                                        )
                                    }
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-xl" />
                                    Cadastrar unidade
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'alerts' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50">
                    {alerts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 p-8">
                            <MdCheckCircle className="text-6xl text-success" />
                            <div className="text-center">
                                <h3 className="text-lg font-semibold text-base-content mb-1">
                                    Nenhum alerta ativo
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    Todas as unidades estão dentro dos
                                    parâmetros
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Unidade</th>
                                        <th>Tipo</th>
                                        <th>Temperatura</th>
                                        <th>Data/Hora</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alerts.map(alert => (
                                        <tr key={alert.id} className="hover">
                                            <td>
                                                {alert.storageUnit?.name || '-'}
                                            </td>
                                            <td>
                                                <span className="badge badge-error">
                                                    {alert.type === 'TEMP_HIGH'
                                                        ? 'Temperatura Alta'
                                                        : alert.type ===
                                                            'TEMP_LOW'
                                                            ? 'Temperatura Baixa'
                                                            : alert.type ===
                                                                'TEMP_CRITICAL'
                                                                ? 'Critico'
                                                                : 'Sensor Offline'}
                                                </span>
                                            </td>
                                            <td>
                                                {alert.temperature
                                                    ? `${alert.temperature.toFixed(1)}C`
                                                    : '-'}
                                            </td>
                                            <td>
                                                {formatDateTime(
                                                    alert.triggeredAt
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge ${TEMPERATURE_ALERT_STATUS_COLORS[alert.status]}`}
                                                >
                                                    {
                                                        TEMPERATURE_ALERT_STATUS_LABELS[
                                                        alert.status
                                                        ]
                                                    }
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
